import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ModalBodyClass } from '../../../modals/modal-body';
import { Observable, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AccountType, AutoAttendantEntitySearchResult, UserPhoto } from '../../../shared/models/generated/ziroModels';
import { ZiroFormConfig } from '../../../forms/ziro-forms-models';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../forms/fields/select/ziro-select.component';
import { ZiroFormsModule } from '../../../forms/ziro-forms.module';
import { TranslateModule } from '@ngx-translate/core';
import { DetailedModalComponent } from '../../../modals/detailed-modal/detailed-modal.component';
import { MicrosoftTeamsUserSearchResource } from '../../../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { Microsoft365UserResource } from '../../../helpdesk/shared/resources/microsoft-365-user.resource';
import { getUserInitialsFromDisplayName } from '../../../shared/utils';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZpmUserPhotoContext } from '../../../shared/contexts/zpm-user-photo.context';
import { TeamsAutoAttendantsAccountSearchResource } from '../../resources/teams-auto-attendants-account-search.resource';

export interface AuthorizedUserDisplay {
  guid: string;
  displayName: string;
  upn: string;
  photoBase64: string;
}

interface AuthorizedUsersFormData {
  searchTerm: ZiroSelectOption | null;
}

@Component({
  selector: 'ziro-authorized-users-modal',
  imports: [ZiroFormsModule, TranslateModule],
  templateUrl: './authorized-users-modal.component.html',
  styleUrl: './authorized-users-modal.component.scss',
  providers: [MicrosoftTeamsUserSearchResource, Microsoft365UserResource],
})
export class AuthorizedUsersModalComponent
  extends ModalBodyClass<AuthorizedUsersFormData>
  implements OnInit, OnDestroy
{
  private _modalComponent = inject(DetailedModalComponent<any>);
  private _teamsAutoAttendantsAccountSearchResource = inject(TeamsAutoAttendantsAccountSearchResource);
  private _zpmUserPhotoContext = inject(ZpmUserPhotoContext);

  private _tenantId: string;
  private _subscriptions = new Subscription();
  private _latestSearchResults: AutoAttendantEntitySearchResult[] = [];
  private _latestPhotos: UserPhoto[] = [];

  readonly localUsers = signal<AuthorizedUserDisplay[]>([]);

  formConfig: ZiroFormConfig;

  ngOnInit(): void {
    this._tenantId = this._modalComponent.modalViewProperties?.tenantId;
    const initialUsers: AuthorizedUserDisplay[] = this._modalComponent.modalViewProperties?.initialUsers ?? [];
    this.localUsers.set([...initialUsers]);

    this.formConfig = {
      fields: {
        searchTerm: {
          label: () => 'tkey;admin.teams_voice_management.teams_auto_attendant.authorized_users.search.label',
          dataAutomation: 'authorized-users-search',
          componentConfig: new ZiroSelectConfig({
            options: [],
            isMultiSelect: false,
            asyncOptionsFn: (searchTerm) => this._getUserAsync(searchTerm),
            minSearchLength: 3,
            clearWithInput: true,
            placeholder: 'tkey;admin.teams_voice_management.teams_auto_attendant.authorized_users.search.placeholder',
          }),
        },
      },
    };

    this.entitySource.next({ searchTerm: null });

    const updateSub = this.ziroFormsUpdate$.subscribe((update) => {
      const selected = update.new?.searchTerm as ZiroSelectOption | null;
      if (selected?.value) {
        const upn = selected.value as string;
        const result = this._latestSearchResults.find((r) => r.userPrincipalName === upn);
        if (result && !this.localUsers().some((u) => u.guid === result.guid)) {
          const photo = this._latestPhotos.find((p) => p.userPrincipalName === upn)?.photoBase64 ?? '';
          this.localUsers.update((users) => [
            ...users,
            {
              guid: result.guid,
              displayName: result.displayName ?? result.userPrincipalName,
              upn: result.userPrincipalName,
              photoBase64: photo,
            },
          ]);
        }
        this.entitySource.next({ searchTerm: null });
      }
    });
    this._subscriptions.add(updateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  removeUser(guid: string): void {
    this.localUsers.update((users) => users.filter((u) => u.guid !== guid));
  }

  getUserInitials(displayName: string): string {
    return getUserInitialsFromDisplayName(displayName);
  }

  submit(): Observable<AuthorizedUserDisplay[]> {
    return this._modalComponent.modalViewProperties.submitFn(this.localUsers());
  }

  private _getUserAsync(searchTerm: string): Observable<ZiroSelectOption[]> {
    return this._teamsAutoAttendantsAccountSearchResource.search(this._tenantId, searchTerm, AccountType.USER).pipe(
      switchMap((results) => {
        this._latestSearchResults = results.filter((r) => !this.localUsers().some((u) => u.guid === r.guid));
        if (!this._latestSearchResults.length) {
          this._latestPhotos = [];
          return of([] as UserPhoto[]);
        }
        const upns = this._latestSearchResults.map((r) => r.userPrincipalName);
        return this._zpmUserPhotoContext.initAutoAttendantUserPhotos(this._tenantId, upns).pipe(
          map(() => {
            this._latestPhotos = this._zpmUserPhotoContext.getUserPhotos();
            return this._latestPhotos;
          }),
        );
      }),
      map((photos) =>
        this._latestSearchResults.map((result) => {
          const photo = photos.find((p) => p.userPrincipalName === result.userPrincipalName)?.photoBase64 ?? '';
          return {
            label: result.displayName ?? result.userPrincipalName,
            value: result.userPrincipalName,
            photoBase64: photo,
            displayIcon: !photo,
            appendValue: true,
            disabled: !result.hasValidLicense || !result.hasPhoneNumber,
            disabledTooltip:
              !result.hasValidLicense || !result.hasPhoneNumber
                ? 'tkey;admin.teams_voice_management.teams_auto_attendant.authorized_users.search.not_enterprise_voice_enabled'
                : '',
          };
        }),
      ),
    );
  }

  protected readonly ZiroIcons = ZiroIcons;
}
